/** Triggered when a cell starts editing, allowing validation and state updates. */
export const onCellEditingStarted = (
  event,
  fieldsToCheckForCellEditingStartedEvent,
  updatedRowData,
  rowData,
  setUpdatedRowData,
  handleGridEditingStatus = () => {}
) => {
  if (fieldsToCheckForCellEditingStartedEvent.includes(event.colDef.field)) {
    let columnName = `original_${event.colDef.field}`;
    let modifiedData =
      updatedRowData && updatedRowData.length > 0
        ? JSON.parse(JSON.stringify(updatedRowData))
        : JSON.parse(JSON.stringify(rowData));
    let matchedRow = modifiedData.find(
      (row) => row.uniqueKey === event.data.uniqueKey
    );

    if (
      matchedRow &&
      !Object.prototype.hasOwnProperty.call(matchedRow, columnName)
    ) {
      matchedRow[columnName] = event.value;
      setUpdatedRowData(modifiedData);
    }
  }

  // Update the grid editing status after editing is started
  handleGridEditingStatus();
};

/** Triggered when a cell finishes editing, allowing validation and state updates. */
export const onCellEditingStopped = (
  event,
  fieldsToCheckForCellEditingStoppedEvent,
  updatedRowData,
  rowData,
  setUpdatedRowData,
  setRowData,
  handleGridEditingStatus = () => {}
) => {
  // handle onCellEditingStopped event for MotifSelect and MotifInput components as onCellValueChanged is not working for that component.
  if (fieldsToCheckForCellEditingStoppedEvent.includes(event.colDef.field)) {
    // Early exit: if editing stopped on propertyAmenityName and value is unchanged do nothing further.
    if (event?.colDef?.field === 'propertyAmenityName') {
      const matchedUpdated = Array.isArray(updatedRowData)
        ? updatedRowData.find((r) => r.uniqueKey === event.data?.uniqueKey)
        : null;
      if (
        matchedUpdated &&
        matchedUpdated.propertyAmenityName === event.data?.propertyAmenityName
      ) {
        handleGridEditingStatus();
        return; // No changes to process for unchanged propertyAmenityName.
      }
    }
    let modifiedData = JSON.parse(JSON.stringify(rowData));
    let matchedRowToBeUpdated = modifiedData.find(
      (row) => row.uniqueKey === event.data.uniqueKey
    );

    // On cell editing stopped, the new value selected in SELECT component or INPUT component is updated in updatedRowData. e.g.TRSName or ServiceName
    // So sync that value to the original rowData as well.
    let sourceData =
      updatedRowData && updatedRowData.length > 0
        ? JSON.parse(JSON.stringify(updatedRowData))
        : [];
    let matchedRowSource = sourceData.find(
      (row) => row.uniqueKey === event.data.uniqueKey
    );
    let rowDataUpdatedFlag = false;
    if (matchedRowToBeUpdated && matchedRowSource) {
      matchedRowToBeUpdated[event.colDef.field] =
        matchedRowSource[event.colDef.field];
      rowDataUpdatedFlag = true;
    }

    // This is to update the Service Name - Other description column if the value is changed in the select component to value other than "Other"
    if (
      event.colDef.field === 'propertyAmenityName' &&
      matchedRowSource[event.colDef.field] !== 'Other' &&
      matchedRowToBeUpdated &&
      matchedRowSource
    ) {
      matchedRowToBeUpdated['serviceNameOtherDescription'] = '';
      matchedRowSource['serviceNameOtherDescription'] = '';
      rowDataUpdatedFlag = true;
    }

    // This is to update the TRS ID in the rowData if the TRS Name is changed in the select component.
    if (
      event.colDef.field === 'trsName' &&
      matchedRowToBeUpdated &&
      matchedRowSource
    ) {
      matchedRowToBeUpdated['taxableREITSubsidiaryID'] =
        matchedRowSource['taxableREITSubsidiaryID'];
      rowDataUpdatedFlag = true;
    }

    // Handle error messages for Service Name and TRS Name
    if (event.colDef.field === 'propertyAmenityName') {
      // Clear all previously selected service provider options (Customary, Employee, IK, TRS, TRS Name) for that row
      matchedRowToBeUpdated['isProvidedByCustomary'] = false;
      matchedRowToBeUpdated['isProvidedByEmployee'] = false;
      matchedRowToBeUpdated['isProvidedByIndependentContractor'] = false;
      matchedRowToBeUpdated['isProvidedByTaxableReitSubsidiary'] = false;
      matchedRowToBeUpdated['trsName'] = '';
      matchedRowToBeUpdated['taxableREITSubsidiaryID'] = -1;
      rowDataUpdatedFlag = true;

      if (matchedRowSource[event.colDef.field] === '') {
        matchedRowToBeUpdated[`${event.colDef.field}_error`] =
          'Please select Service Name.';

        // Reset error message for Service Name Other Description
        matchedRowToBeUpdated['serviceNameOtherDescription_error'] = '';
        rowDataUpdatedFlag = true;
      } else {
        matchedRowToBeUpdated[`${event.colDef.field}_error`] = '';
        matchedRowToBeUpdated['serviceNameOtherDescription_error'] = '';

        if (matchedRowSource[event.colDef.field] === 'Other') {
          if (matchedRowSource['serviceNameOtherDescription']?.trim() === '') {
            // Set error message for Service Name Other Description
            matchedRowToBeUpdated['serviceNameOtherDescription_error'] =
              'Please enter Service Name Other Description.';
          } else if (
            matchedRowSource['serviceNameOtherDescription'] &&
            matchedRowSource['serviceNameOtherDescription']?.trim().length > 255
          ) {
            // Set max length error message for Service Name Other Description
            matchedRowToBeUpdated['serviceNameOtherDescription_error'] =
              'Maximum 255 characters allowed.';
          } else {
            // Check for duplicate Service Name Other Description within the same Property ID
            const foundDuplicateServiceOther = modifiedData.some(
              ({ uniqueKey, propertyID, serviceNameOtherDescription }) =>
                uniqueKey !== matchedRowToBeUpdated.uniqueKey &&
                propertyID === matchedRowToBeUpdated.propertyID &&
                serviceNameOtherDescription?.trim().toLowerCase() ===
                  matchedRowToBeUpdated.serviceNameOtherDescription
                    ?.trim()
                    .toLowerCase()
            );

            matchedRowToBeUpdated['serviceNameOtherDescription_error'] =
              foundDuplicateServiceOther
                ? `Duplicate service name - other description exists for property ${matchedRowToBeUpdated.propertyName}.`
                : '';
          }
        } else {
          // Check for duplicate Service Name within the same Property ID
          const foundDuplicateService = modifiedData.some(
            ({ uniqueKey, propertyID, propertyAmenityName }) =>
              uniqueKey !== matchedRowToBeUpdated.uniqueKey &&
              propertyID === matchedRowToBeUpdated.propertyID &&
              propertyAmenityName === matchedRowToBeUpdated.propertyAmenityName
          );

          // Set error message if a duplicate is found
          matchedRowToBeUpdated[`${event.colDef.field}_error`] =
            foundDuplicateService
              ? `Duplicate service name exists for property ${matchedRowToBeUpdated.propertyName}.`
              : '';
        }
        rowDataUpdatedFlag = true;
      }
    }

    if (event.colDef.field === 'serviceNameOtherDescription') {
      if (
        matchedRowSource['propertyAmenityName'] === 'Other' &&
        matchedRowSource[event.colDef.field]?.trim() === ''
      ) {
        // Set error message for Service Name Other Description
        matchedRowToBeUpdated[`${event.colDef.field}_error`] =
          'Please enter Service Name Other Description.';
      } else if (
        matchedRowSource['propertyAmenityName'] === 'Other' &&
        matchedRowSource[event.colDef.field] &&
        matchedRowSource[event.colDef.field]?.trim().length > 255
      ) {
        // Set max length error message for Service Name Other Description
        matchedRowToBeUpdated[`${event.colDef.field}_error`] =
          'Maximum 255 characters allowed.';
      } else {
        // Check for duplicate Service Name Other Description within the same Property ID
        const foundDuplicateServiceOther = modifiedData.some(
          ({ uniqueKey, propertyID, serviceNameOtherDescription }) =>
            uniqueKey !== matchedRowToBeUpdated.uniqueKey &&
            propertyID === matchedRowToBeUpdated.propertyID &&
            serviceNameOtherDescription?.trim().toLowerCase() ===
              matchedRowToBeUpdated.serviceNameOtherDescription
                ?.trim()
                .toLowerCase()
        );

        matchedRowToBeUpdated[`${event.colDef.field}_error`] =
          foundDuplicateServiceOther
            ? `Duplicate service name - other description exists for property ${matchedRowToBeUpdated.propertyName}.`
            : '';
      }
      rowDataUpdatedFlag = true;
    }

    if (rowDataUpdatedFlag) {
      setRowData(modifiedData);
      setUpdatedRowData(sourceData);
    }
  }

  // Update the grid editing status after editing is stopped
  handleGridEditingStatus();
};

/** Triggered when a cell value changes, enabling data updates. */
export const onCellValueChanged = (
  event,
  fieldsToCheckForCellValueChangedEvent,
  updatedRowData,
  rowData,
  setUpdatedRowData,
  setRowData
) => {
  if (fieldsToCheckForCellValueChangedEvent.includes(event.colDef.field)) {
    let rowDataArray = JSON.parse(JSON.stringify(rowData));
    let targetRow = rowDataArray.find(
      (row) => row.uniqueKey === event.data.uniqueKey
    );
    let updateRowDataFlag = false;

    if (event.source === 'edit') {
      // Handle validation for specific fields like isProvidedByTaxableReitSubsidiary checkbox
      if (event.colDef.field === 'isProvidedByTaxableReitSubsidiary') {
        if (event.newValue === true) {
          // If isProvidedByTaxableReitSubsidiary is set to true, then set TRS Name field in edit mode
          event.api.startEditingCell({
            rowIndex: event.rowIndex,
            colKey: 'trsName',
          });
        } else {
          if (targetRow) {
            // Reset TRS Name and TRS ID if isProvidedByTaxableReitSubsidiary is set to false
            targetRow['trsName'] = '';
            targetRow['taxableREITSubsidiaryID'] = -1;

            updateRowDataFlag = true;
            // On selection/deselection of checkbox set that checkbox's column in edit mode
            event.api.startEditingCell({
              rowIndex: event.rowIndex,
              colKey: event.colDef.field,
            });
          }
        }
      } else {
        // On election/deselection of checkbox set that checkbox's column in edit mode
        event.api.startEditingCell({
          rowIndex: event.rowIndex,
          colKey: event.colDef.field,
        });
      }
    }

    // Proceed with modifying the updatedRowData. The updatedRowData is used to keep track of the changes made in the grid.
    const columnName = 'original_' + event.colDef.field;
    let changeTrackerArray =
      updatedRowData && updatedRowData.length > 0
        ? JSON.parse(JSON.stringify(updatedRowData))
        : JSON.parse(JSON.stringify(rowData));
    let changeTrackerRow = changeTrackerArray.find(
      (row) => row.uniqueKey === event.data.uniqueKey
    );

    if (changeTrackerRow) {
      if (!Object.prototype.hasOwnProperty.call(changeTrackerRow, columnName)) {
        changeTrackerRow[columnName] = event.oldValue;
      }

      changeTrackerRow[event.colDef.field] = event.newValue;

      // Handle special conditions for isProvidedByTaxableReitSubsidiary to modify updatedRowData
      if (
        event.colDef.field === 'isProvidedByTaxableReitSubsidiary' &&
        event.newValue === false &&
        event.source === 'edit'
      ) {
        changeTrackerRow['original_trsName'] = changeTrackerRow['trsName'];

        changeTrackerRow['trsName'] = '';
        changeTrackerRow['taxableREITSubsidiaryID'] = -1;
      }
      setUpdatedRowData(changeTrackerArray);
    }

    if (updateRowDataFlag) {
      // Update the rowData with the modified data
      setRowData(rowDataArray);
    }
  }
};
